<template>
  <div class="hierarchy-view-wrapper">
    <div v-if="ds != null" class="hierarchy-panel">
      <div class="hierarchy-panel__header">
        <div class="hierarchy-panel__title">
          <div class="hierarchy-panel__icon">
            <i class="fas fa-sitemap"></i>
          </div>
          <div>
            <h4 class="hierarchy-panel__heading">Organization Hierarchy</h4>
            <div class="hierarchy-panel__subtitle">
              Navigate platforms, organizations, branches and team members at a glance.
            </div>
          </div>
        </div>
        <div class="hierarchy-panel__toolbar">
          <button class="pill-btn" @click="expandAll">
            <i class="fas fa-expand-arrows-alt"></i>
            <span>Expand all</span>
          </button>
          <button class="pill-btn" @click="collapseAll">
            <i class="fas fa-compress-arrows-alt"></i>
            <span>Collapse all</span>
          </button>
          <button
              :class="['pill-btn', 'pill-btn--toggle', { 'pill-btn--active': horizontal }]"
              @click="horizontal = !horizontal"
          >
            <i :class="horizontal ? 'fas fa-arrows-alt-h' : 'fas fa-arrows-alt-v'"></i>
            <span>{{ horizontal ? 'Horizontal' : 'Vertical' }}</span>
          </button>
        </div>
      </div>

      <div class="hierarchy-panel__legend">
        <span class="legend-item legend-item--platform"><span class="legend-dot"></span>Platform</span>
        <span class="legend-item legend-item--desk"><span class="legend-dot"></span>Desk</span>
        <span class="legend-item legend-item--organization"><span class="legend-dot"></span>Organization</span>
        <span class="legend-item legend-item--branch"><span class="legend-dot"></span>Branch</span>
        <span class="legend-item legend-item--admin"><span class="legend-dot"></span>Admin</span>
        <span class="legend-item legend-item--manager"><span class="legend-dot"></span>Manager</span>
        <span class="legend-item legend-item--agent"><span class="legend-dot"></span>Agent</span>
      </div>

      <div class="hierarchy-panel__canvas">
        <div class="orgchart-scroll-wrapper">
          <div class="orgchart-root">
            <org-node
                :node="ds"
                :collapsed-map="collapsedMap"
                :horizontal="horizontal"
                @toggle="toggleNode"
                @navigate="navigateToUser"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="hierarchy-loading">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading hierarchy...</div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import OrgNode from './OrgNode.vue';

export default {
  name: 'HierarchyView2',
  components: { OrgNode },
  data() {
    return {
      ds: null,
      collapsedMap: {},
      horizontal: true,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    ...mapActions('businessesUnits', ['getHierarchy']),
    navigateToUser(id) {
      this.$router.push('/configurations/users?userId=' + id + '&tab=0');
    },
    loadData() {
      this.getHierarchy().then((response) => {
        this.ds = response;
      });
    },
    toggleNode(key) {
      this.$set(this.collapsedMap, key, !this.collapsedMap[key]);
    },
    collectKeys(node, result) {
      const key = node.id + '_' + node.title;
      result.push(key);
      if (node.children) {
        node.children.forEach((c) => this.collectKeys(c, result));
      }
    },
    expandAll() {
      this.collapsedMap = {};
    },
    collapseAll() {
      const keys = [];
      this.collectKeys(this.ds, keys);
      const map = {};
      keys.forEach((k) => (map[k] = true));
      this.collapsedMap = map;
    },
  },
};
</script>

<style lang="scss">
.hierarchy-view-wrapper {
  padding: 0.5rem;
}

.hierarchy-panel {
  background: #ffffff;
  border: 1px solid rgba(0, 123, 255, 0.1);
  border-radius: 14px;
  box-shadow: 0 8px 24px -18px rgba(0, 123, 255, 0.4);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 1.15rem;
    border-bottom: 1px solid rgba(0, 123, 255, 0.08);
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.05), rgba(0, 123, 255, 0.01));
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    box-shadow: 0 8px 14px -8px rgba(0, 123, 255, 0.5);
  }

  &__heading {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__subtitle {
    font-size: 0.78rem;
    color: #b4b7bd;
    margin-top: 0.1rem;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.9rem;
    padding: 0.6rem 1.15rem;
    border-bottom: 1px solid rgba(0, 123, 255, 0.06);
    background: rgba(247, 249, 255, 0.7);
  }

  &__canvas {
    flex: 1;
    min-height: 480px;
    background:
        radial-gradient(circle at 1px 1px, rgba(0, 123, 255, 0.08) 1px, transparent 0) 0 0 / 22px 22px,
        linear-gradient(180deg, #f8f8f8 0%, #f6f6f6 100%);
  }
}

.pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 999px;
  border: 1px solid rgba(0, 123, 255, 0.2);
  background: #ffffff;
  color: #6e6b7b;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 0.72rem;
  }

  &:hover {
    border-color: rgba(0, 123, 255, 0.45);
    color: #007BFF;
    background: rgba(0, 123, 255, 0.05);
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -10px rgba(0, 123, 255, 0.55);
  }

  &--active {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
    color: #fff !important;
    border-color: transparent;
    box-shadow: 0 8px 16px -10px rgba(0, 123, 255, 0.7);

    &:hover {
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      color: #fff;
    }
  }
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b4b7bd;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  }

  &--platform .legend-dot { background: #00cfe8; }
  &--desk .legend-dot { background: #7367f0; }
  &--organization .legend-dot { background: #007BFF; }
  &--branch .legend-dot { background: #ff9f43; }
  &--admin .legend-dot { background: #ea5455; }
  &--manager .legend-dot { background: #28c76f; }
  &--agent .legend-dot { background: #b4b7bd; }
}

.orgchart-scroll-wrapper {
  width: 100%;
  overflow: auto;
  padding: 1.75rem;
}

.orgchart-root {
  min-width: max-content;
}

.hierarchy-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  gap: 0.9rem;
}

.loading-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(0, 123, 255, 0.15);
  border-top-color: #007BFF;
  border-radius: 50%;
  animation: hier-spin 0.8s linear infinite;
}

@keyframes hier-spin { to { transform: rotate(360deg); } }

.loading-text {
  font-size: 0.85rem;
  color: #b4b7bd;
  font-weight: 500;
}

/* ===== Dark mode ===== */
.dark-layout {
  .hierarchy-panel {
    background: #283046;
    border-color: rgba(77, 163, 255, 0.15);
    box-shadow: 0 8px 24px -18px rgba(0, 0, 0, 0.6);

    &__header {
      border-bottom-color: rgba(77, 163, 255, 0.12);
      background: linear-gradient(135deg, rgba(77, 163, 255, 0.1), rgba(77, 163, 255, 0.02));
    }

    &__heading { color: #d0d2d6; }
    &__subtitle { color: #a0a0a0; }

    &__legend {
      background: rgba(38, 47, 70, 0.65);
      border-bottom-color: rgba(77, 163, 255, 0.08);
    }

    &__canvas {
      background:
          radial-gradient(circle at 1px 1px, rgba(77, 163, 255, 0.07) 1px, transparent 0) 0 0 / 22px 22px,
          linear-gradient(180deg, #232a3d 0%, #1e2536 100%);
    }
  }

  .pill-btn {
    background: rgba(77, 163, 255, 0.06);
    color: #b4b7bd;
    border-color: rgba(77, 163, 255, 0.2);

    &:hover {
      background: rgba(77, 163, 255, 0.15);
      color: #e4ecff;
      border-color: rgba(77, 163, 255, 0.45);
    }

    &--active {
      color: #fff !important;
    }
  }

  .legend-item { color: #a0a0a0; }

  .loading-text { color: #a0a0a0; }
  .loading-spinner { border-color: rgba(77, 163, 255, 0.2); border-top-color: #4da3ff; }
}

/* ============================================ */
/* Tree connectors + layout (unchanged behavior) */
/* ============================================ */
.org-tree-node {
  display: flex;
  flex-direction: column;
}

.org-tree-row {
  display: flex;
  align-items: center;
}

.org-tree-children {
  padding-left: 34px;
  position: relative;
}

.org-tree-child {
  display: flex;
  align-items: stretch;
  position: relative;
}

.org-tree-connector {
  width: 26px;
  flex-shrink: 0;
  position: relative;
}

.org-tree-connector::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px dashed rgba(0, 123, 255, 0.35);
}

.org-tree-connector .connector-line {
  position: absolute;
  top: 28px;
  left: 0;
  width: 100%;
  height: 0;
  border-top: 2px dashed rgba(0, 123, 255, 0.35);
}

.org-tree-child:first-child > .org-tree-connector::before { top: 28px; }
.org-tree-child:last-child > .org-tree-connector::before { bottom: auto; height: 28px; }
.org-tree-child:only-child > .org-tree-connector::before { display: none; }

/* Horizontal mode */
.org-tree-node--horizontal {
  flex-direction: row;
  align-items: flex-start;
}

.org-tree-node--horizontal > .org-tree-row { flex-shrink: 0; }

.org-tree-children--horizontal {
  padding-left: 0;
  display: flex;
  flex-direction: column;
}

.org-tree-connector--horizontal {
  width: 32px;
  align-self: stretch;
}

.org-tree-connector--horizontal::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px dashed rgba(0, 123, 255, 0.35);
}

.org-tree-connector--horizontal .connector-line {
  position: absolute;
  top: 28px;
  left: 0;
  width: 100%;
  height: 0;
  border-top: 2px dashed rgba(0, 123, 255, 0.35);
  background: none;
  border-radius: 0;
}

.org-tree-child:first-child > .org-tree-connector--horizontal::before { top: 28px; }
.org-tree-child:last-child > .org-tree-connector--horizontal::before { bottom: auto; height: 28px; }
.org-tree-child:only-child > .org-tree-connector--horizontal::before { display: none; }

.org-tree-node--horizontal > .org-tree-row .org-card__toggle {
  right: auto;
  bottom: -12px;
  top: auto;
  left: 50%;
  transform: translateX(-50%);
}

.dark-layout {
  .org-tree-connector::before,
  .org-tree-connector--horizontal::before,
  .org-tree-connector .connector-line,
  .org-tree-connector--horizontal .connector-line {
    border-color: rgba(77, 163, 255, 0.3);
  }
}

/* ========================================== */
/* Cards                                       */
/* ========================================== */
.org-card {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  min-width: 210px;
  max-width: 280px;
  margin: 6px 0;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(0, 123, 255, 0.12);
  box-shadow: 0 4px 14px -10px rgba(0, 123, 255, 0.35);
  transition: all 0.2s ease;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 12px 0 0 12px;
    background: linear-gradient(180deg, #4da3ff, #007BFF);
    opacity: 0.85;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 24px -14px rgba(0, 123, 255, 0.5);
    border-color: rgba(0, 123, 255, 0.25);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.55rem 0.85rem 0.3rem 1rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #b4b7bd;
  }

  &__icon {
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: rgba(0, 123, 255, 0.1);
    color: #007BFF;
    font-size: 0.72rem;
  }

  &__title {
    flex: 1;
    font-size: 0.7rem;
  }

  &__badge {
    background: rgba(0, 123, 255, 0.12);
    color: #007BFF;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    min-width: 22px;
    text-align: center;
    line-height: 1.3;
  }

  &__body {
    padding: 0.15rem 0.85rem 0.7rem 1rem;
  }

  &__name {
    font-size: 0.88rem;
    font-weight: 600;
    color: #5e5873;
    word-break: break-word;
    line-height: 1.35;
  }

  &__name--link {
    color: #007BFF;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.15s;

    &:hover {
      text-decoration: underline;
      color: #0062cc;
    }
  }

  /* Toggle button */
  &__toggle {
    position: absolute;
    right: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid rgba(0, 123, 255, 0.25);
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    transition: all 0.15s;
    padding: 0;
    box-shadow: 0 4px 10px -6px rgba(0, 123, 255, 0.5);

    &:hover {
      border-color: #007BFF;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);

      .toggle-arrow--down { border-top-color: #fff; }
      .toggle-arrow--right { border-left-color: #fff; }
    }
  }

  /* Node type variants */
  &--platform {
    &::before { background: linear-gradient(180deg, #66dcf0, #00cfe8); }
    .org-card__icon { background: rgba(0, 207, 232, 0.15); color: #00cfe8; }
    .org-card__header { color: #0095a8; }
    .org-card__badge { background: rgba(0, 207, 232, 0.15); color: #00cfe8; }
  }

  &--desk {
    &::before { background: linear-gradient(180deg, #9e95f5, #7367f0); }
    .org-card__icon { background: rgba(115, 103, 240, 0.15); color: #7367f0; }
    .org-card__header { color: #5e50ee; }
    .org-card__badge { background: rgba(115, 103, 240, 0.15); color: #7367f0; }
  }

  &--organization {
    &::before { background: linear-gradient(180deg, #4da3ff, #007BFF); }
    .org-card__icon { background: rgba(0, 123, 255, 0.12); color: #007BFF; }
    .org-card__header { color: #007BFF; }
    .org-card__badge { background: rgba(0, 123, 255, 0.12); color: #007BFF; }
  }

  &--branch {
    &::before { background: linear-gradient(180deg, #ffbb79, #ff9f43); }
    .org-card__icon { background: rgba(255, 159, 67, 0.15); color: #ff9f43; }
    .org-card__header { color: #cf7d17; }
    .org-card__badge { background: rgba(255, 159, 67, 0.15); color: #cf7d17; }
  }

  &--admin {
    &::before { background: linear-gradient(180deg, #ff7a7b, #ea5455); }
    .org-card__icon { background: rgba(234, 84, 85, 0.12); color: #ea5455; }
    .org-card__header { color: #c53e3e; }
    .org-card__badge { background: rgba(234, 84, 85, 0.12); color: #c53e3e; }
  }

  &--manager {
    &::before { background: linear-gradient(180deg, #5fdc95, #28c76f); }
    .org-card__icon { background: rgba(40, 199, 111, 0.15); color: #28c76f; }
    .org-card__header { color: #1e9d55; }
    .org-card__badge { background: rgba(40, 199, 111, 0.15); color: #1e9d55; }
  }

  &--agent {
    min-width: 180px;
    &::before { background: linear-gradient(180deg, #b4b7bd, #b4b7bd); }
    .org-card__icon { background: rgba(110, 117, 145, 0.15); color: #6e6b7b; }
    .org-card__header { color: #b4b7bd; }
    .org-card__badge { background: rgba(110, 117, 145, 0.12); color: #6e6b7b; }
    .org-card__name { font-size: 0.82rem; font-weight: 500; }
  }

  &--default {
    &::before { background: linear-gradient(180deg, #b4b7bd, #b4b7bd); }
  }
}

.toggle-arrow {
  display: block;
  width: 0;
  height: 0;
  border-style: solid;
  transition: border-color 0.15s;
}

.toggle-arrow--down {
  border-width: 5px 4px 0 4px;
  border-color: #6e6b7b transparent transparent transparent;
}

.toggle-arrow--right {
  border-width: 4px 0 4px 5px;
  border-color: transparent transparent transparent #6e6b7b;
}

/* Dark mode for cards */
.dark-layout {
  .org-card {
    background: #313a54;
    border-color: rgba(77, 163, 255, 0.18);
    box-shadow: 0 4px 14px -10px rgba(0, 0, 0, 0.6);

    &:hover {
      border-color: rgba(77, 163, 255, 0.38);
      box-shadow: 0 14px 24px -14px rgba(0, 0, 0, 0.7);
    }

    &__name { color: #d0d2d6; }
    &__name--link { color: #4da3ff; &:hover { color: #80bdff; } }
    &__header { color: #a0a0a0; }
    &__toggle {
      background: #313a54;
      border-color: rgba(77, 163, 255, 0.3);
    }
  }

  .toggle-arrow--down { border-top-color: #b4b7bd; }
  .toggle-arrow--right { border-left-color: #b4b7bd; }
}
</style>
